import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo_golden_white.png';
import '../styles/KioskMode.css';

export function KioskCodeEntryPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  // Focus first input on mount
  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (digit && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 4 digits entered
    if (digit && index === 3) {
      const fullCode = [...newCode.slice(0, 3), digit].join('');
      if (fullCode.length === 4) {
        validateCode(fullCode);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace - go to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pastedData.length === 4) {
      const newCode = pastedData.split('');
      setCode(newCode);
      validateCode(pastedData);
    }
  };

  const validateCode = async (fullCode: string) => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';
      const response = await fetch(`${apiUrl}/kiosk/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: fullCode })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Invalid code');
      }

      // Store session token and navigate to intake form
      sessionStorage.setItem('kiosk_session', JSON.stringify({
        token: result.data.sessionToken,
        staffId: result.data.staffId,
        expiresAt: result.data.expiresAt
      }));

      navigate('/kiosk/intake');
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
      setCode(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length === 4) {
      validateCode(fullCode);
    } else {
      setError('Please enter all 4 digits');
    }
  };

  return (
    <div className="kiosk-page" style={{ justifyContent: 'center' }}>
      <div className="kiosk-container" style={{ maxWidth: 500, textAlign: 'center' }}>
        <header className="kiosk-header">
          <img src={logo} alt="Belafonte TACOLCY Center" />
          <h1>Client Intake</h1>
          <p>Enter the 4-digit code provided by staff</p>
        </header>

        <form onSubmit={handleSubmit} className="kiosk-form-card">
          {error && (
            <div className="kiosk-alert kiosk-alert-error">
              {error}
            </div>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.75rem',
            marginBottom: '2rem'
          }}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={loading}
                style={{
                  width: 70,
                  height: 80,
                  fontSize: '2.5rem',
                  textAlign: 'center',
                  border: '3px solid #e0e0e0',
                  borderRadius: 12,
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  transition: 'all 0.2s ease'
                }}
                className="kiosk-code-input"
              />
            ))}
          </div>

          <button
            type="submit"
            className="kiosk-button kiosk-button-primary"
            disabled={loading || code.join('').length !== 4}
            style={{ width: '100%' }}
          >
            {loading ? 'Validating...' : 'Continue'}
          </button>

          <p style={{ marginTop: '1.5rem', color: '#666', fontSize: '0.9rem' }}>
            Don&apos;t have a code? Please ask a staff member for assistance.
          </p>
        </form>
      </div>

      {/* Help button */}
      <button
        type="button"
        className="kiosk-help-button"
        onClick={() => alert('Please ask a staff member for assistance.')}
        aria-label="Get help"
      >
        ?
      </button>
    </div>
  );
}
